/**
 * Configuration History Component
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Compare as CompareIcon,
  RestoreFromTrash as RestoreIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { DeviceConfigHistory, DeviceConfig } from '../../types/config';
import { configApi } from '../../services/configApi';

interface ConfigHistoryProps {
  deviceId: number;
}

const ConfigHistory: React.FC<ConfigHistoryProps> = ({ deviceId }) => {
  const [history, setHistory] = useState<DeviceConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedConfig, setSelectedConfig] = useState<DeviceConfig | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareConfigs, setCompareConfigs] = useState<[DeviceConfig?, DeviceConfig?]>([]);

  useEffect(() => {
    loadHistory();
  }, [deviceId, page, rowsPerPage]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const result = await configApi.getConfigHistory(
        deviceId, 
        page * rowsPerPage, 
        rowsPerPage
      );
      setHistory(result.configs);
      setTotal(result.configs.length); // Note: This should be total count from API
    } catch (error) {
      console.error('Failed to load configuration history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewConfig = (config: DeviceConfig) => {
    setSelectedConfig(config);
    setViewDialogOpen(true);
  };

  const handleCompareConfigs = (config1: DeviceConfig, config2: DeviceConfig) => {
    setCompareConfigs([config1, config2]);
    setCompareDialogOpen(true);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatConfigValue = (key: string, value: any): string => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'boolean') {
      return value ? 'Enabled' : 'Disabled';
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    return value || 'N/A';
  };

  const ConfigDetailRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
    <Grid container spacing={2} sx={{ py: 0.5 }}>
      <Grid item xs={6}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="body2" color="text.secondary">
          {formatConfigValue(label, value)}
        </Typography>
      </Grid>
    </Grid>
  );

  const CompareRow: React.FC<{ 
    label: string; 
    value1: any; 
    value2: any; 
    different?: boolean;
  }> = ({ label, value1, value2, different = false }) => (
    <Grid container spacing={2} sx={{ py: 0.5, bgcolor: different ? 'warning.50' : 'transparent' }}>
      <Grid item xs={4}>
        <Typography variant="body2" fontWeight={500}>
          {label}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="body2" color={different ? 'warning.main' : 'text.secondary'}>
          {formatConfigValue(label, value1)}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <Typography variant="body2" color={different ? 'warning.main' : 'text.secondary'}>
          {formatConfigValue(label, value2)}
        </Typography>
      </Grid>
    </Grid>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <Box p={3}>
          <Box display="flex" alignItems="center" mb={3}>
            <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Configuration History
            </Typography>
          </Box>

          {history.length === 0 ? (
            <Alert severity="info">
              No configuration history available for this device.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Controller Type</TableCell>
                      <TableCell>GNSS Inputs</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((config) => (
                      <TableRow key={config.id} hover>
                        <TableCell>
                          <Chip
                            label={`v${config.version}`}
                            color={config.is_active ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {format(new Date(config.created_at), 'MMM d, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          {format(new Date(config.updated_at), 'MMM d, yyyy HH:mm')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={config.controller_type.toUpperCase()}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {Array.from({ length: config.gnss_count }, (_, i) => `GNSS${i+1}`).join(', ')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={config.is_active ? 'Active' : 'Archived'}
                            color={config.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewConfig(config)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {!config.is_active && (
                            <Tooltip title="Compare with Current">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const currentConfig = history.find(c => c.is_active);
                                  if (currentConfig) {
                                    handleCompareConfigs(currentConfig, config);
                                  }
                                }}
                              >
                                <CompareIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </>
          )}
        </Box>
      </Paper>

      {/* View Configuration Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configuration Details - Version {selectedConfig?.version}
        </DialogTitle>
        <DialogContent>
          {selectedConfig && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Created: {format(new Date(selectedConfig.created_at), 'MMMM d, yyyy HH:mm:ss')}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Updated: {format(new Date(selectedConfig.updated_at), 'MMMM d, yyyy HH:mm:ss')}
              </Typography>
              
              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>OCXO Control Configuration</Typography>
              <ConfigDetailRow label="OCXO Control Type" value={selectedConfig.ocxo_control_type} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Device Configuration</Typography>
              <ConfigDetailRow label="ADS-B Enabled (Auto)" value={selectedConfig.is_adsb_enabled} />
              <ConfigDetailRow label="Number of Slots" value={selectedConfig.num_slots} />

              <Divider sx={{ my: 2 }} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>GNSS Configuration</Typography>
              <ConfigDetailRow label="GNSS Count" value={selectedConfig.gnss_count} />
              <ConfigDetailRow 
                label="GNSS Inputs" 
                value={Array.from({ length: selectedConfig.gnss_count }, (_, i) => `GNSS${i+1}`).join(', ')} 
              />
              <ConfigDetailRow 
                label="Internal Delays (ns)" 
                value={selectedConfig.gnss_internal_delays.map((delay, i) => `GNSS${i+1}: ${delay}`).join(', ')} 
              />
              <ConfigDetailRow 
                label="Cable Delays (ns)" 
                value={selectedConfig.gnss_cable_delays.map((delay, i) => `GNSS${i+1}: ${delay}`).join(', ')} 
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Delays Configuration</Typography>
              <ConfigDetailRow label="ADS-B Internal Delay (ns)" value={selectedConfig.adsb_internal_delay} />
              <ConfigDetailRow label="ADS-B Cable Delay (ns)" value={selectedConfig.adsb_cable_delay} />
              <ConfigDetailRow label="Ext Ref Internal Delay (ns)" value={selectedConfig.ext_ref_internal_delay} />
              <ConfigDetailRow label="Ext Ref Cable Delay (ns)" value={selectedConfig.ext_ref_cable_delay} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>CGGTTS</Typography>
              <ConfigDetailRow label="Kalman steering on CGGTTS" value={selectedConfig.kalman_cggtts_mode ? 'On' : 'Off'} />
              <ConfigDetailRow label="REF_DLY (ns)" value={selectedConfig.cggtts_ref_dly ?? 0} />
              <ConfigDetailRow
                label="CAB_DLY (ns)"
                value={(selectedConfig.cggtts_cab_dly ?? []).map((d, i) => `GNSS${i + 1}: ${d}`).join(', ')}
              />
              <ConfigDetailRow
                label="INT_DLY (ns)"
                value={(selectedConfig.cggtts_int_dly ?? []).map((p, i) => `GNSS${i + 1}: ${p[0]}/${p[1]}`).join(', ')}
              />
              <ConfigDetailRow label="CAL_ID" value={selectedConfig.cggtts_cal_id ?? 'NA'} />

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>Controller Configuration</Typography>
              <ConfigDetailRow label="Controller Type" value={selectedConfig.controller_type} />
              {selectedConfig.controller_type === 'pi' && (
                <>
                  <ConfigDetailRow label="Proportional Gain" value={selectedConfig.pi_kp} />
                  <ConfigDetailRow label="Integral Gain" value={selectedConfig.pi_ki} />
                </>
              )}
              {selectedConfig.controller_type === 'kalman' && (
                <ConfigDetailRow label="Window Size" value={selectedConfig.kalman_window_size} />
              )}

              {selectedConfig.notes && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>Notes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedConfig.notes}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Compare Configurations Dialog */}
      <Dialog 
        open={compareDialogOpen} 
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Compare Configurations
        </DialogTitle>
        <DialogContent>
          {compareConfigs[0] && compareConfigs[1] && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Typography variant="h6">Property</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6">
                    Version {compareConfigs[0].version} (Current)
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h6">
                    Version {compareConfigs[1].version}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ mb: 2 }} />

              <CompareRow 
                label="OCXO Control Type" 
                value1={compareConfigs[0].ocxo_control_type} 
                value2={compareConfigs[1].ocxo_control_type}
                different={compareConfigs[0].ocxo_control_type !== compareConfigs[1].ocxo_control_type}
              />
              <CompareRow 
                label="Controller Type" 
                value1={compareConfigs[0].controller_type} 
                value2={compareConfigs[1].controller_type}
                different={compareConfigs[0].controller_type !== compareConfigs[1].controller_type}
              />
              <CompareRow 
                label="GNSS Count" 
                value1={compareConfigs[0].gnss_count} 
                value2={compareConfigs[1].gnss_count}
                different={compareConfigs[0].gnss_count !== compareConfigs[1].gnss_count}
              />
              <CompareRow 
                label="Internal Delays" 
                value1={compareConfigs[0].gnss_internal_delays.join(', ')} 
                value2={compareConfigs[1].gnss_internal_delays.join(', ')}
                different={JSON.stringify(compareConfigs[0].gnss_internal_delays) !== JSON.stringify(compareConfigs[1].gnss_internal_delays)}
              />
              <CompareRow 
                label="Cable Delays" 
                value1={compareConfigs[0].gnss_cable_delays.join(', ')} 
                value2={compareConfigs[1].gnss_cable_delays.join(', ')}
                different={JSON.stringify(compareConfigs[0].gnss_cable_delays) !== JSON.stringify(compareConfigs[1].gnss_cable_delays)}
              />
              {/* Add more comparison rows as needed */}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConfigHistory; 